    import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import "./styles/Revenue.css";

    const API_PAYMENTS = "https://okna.co.tz/mrms/Payment.php";
    const API_EXPENSES = "https://okna.co.tz/mrms/Expenses.php";

    export default function Revenue() {
    const token = localStorage.getItem("token");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [revenue, setRevenue] = useState({});
    const [expenses, setExpenses] = useState({});
    const [opening, setOpening] = useState({});
    const [loading, setLoading] = useState(false);

    const paymentMethods = [
        "CASH",
        "M-PESA",
        "MIX BY YAS",
        "SELECOM",
        "CRDB BANK"
    ];

    // Helper function for authenticated fetch with token in URL
    const authFetch = async (url, options = {}) => {
        if (!token) {
        throw new Error("No authentication token found");
        }
        
        // Add token to URL
        const separator = url.includes('?') ? '&' : '?';
        const urlWithToken = `${url}${separator}token=${token}`;
        
        const response = await fetch(urlWithToken, {
        ...options,
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            ...options.headers
        }
        });
        
        // Handle 401 Unauthorized
        if (response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("staff");
        window.location.href = "/";
        throw new Error("Session expired. Please login again.");
        }
        
        return response;
    };

    useEffect(() => {
        if (token) {
        fetchReport();
        } else {
        console.error("No token found");
        }
    }, [selectedDate, token]);

    const formatDate = (date) => {
        return date.toISOString().slice(0, 10);
    };

    const fetchReport = async () => {
        setLoading(true);
        const date = formatDate(selectedDate);

        try {
        console.log("Fetching payments for date:", date);
        // Fetch payments with token in URL
        const payRes = await authFetch(API_PAYMENTS);
        let payments = await payRes.json();
        
        // Handle payments response
        if (payments.error) {
            console.error("Payment API error:", payments.error);
            payments = [];
        } else if (!Array.isArray(payments)) {
            console.error("Payment API returned non-array:", payments);
            payments = [];
        }
        
        console.log(`Payments loaded: ${payments.length}`);

        // Fetch expenses for the selected date with token in URL
        console.log("Fetching expenses for date:", date);
        const expRes = await authFetch(`${API_EXPENSES}?report=day_to_day&start=${date}&end=${date}`);
        let expData = await expRes.json();
        
        // Handle expenses response
        if (expData.error) {
            console.error("Expenses API error:", expData.error);
            expData = { expenses: [] };
        }
        
        const expensesList = expData.expenses || [];
        console.log(`Expenses loaded: ${expensesList.length}`);

        // Calculate all data
        calculateRevenue(payments, date);
        calculateExpenses(expensesList);
        calculateOpeningBalance(payments, expensesList);
        
        } catch (error) {
        console.error("Error fetching report:", error);
        } finally {
        setLoading(false);
        }
    };

    const calculateRevenue = (payments, date) => {
        let rev = {};
        paymentMethods.forEach(m => rev[m] = 0);

        if (Array.isArray(payments)) {
        payments.forEach(p => {
            if (p.status !== "PAID") return;
            const d = p.created_at?.slice(0, 10);
            if (d !== date) return;
            
            let method = p.payment_method;
            // Normalize method names
            if (method === "CRDB_BANK") method = "CRDB BANK";
            if (method === "MIX_BY_YAS") method = "MIX BY YAS";
            
            if (rev.hasOwnProperty(method)) {
            rev[method] += parseFloat(p.total_amount || 0);
            }
        });
        }

        setRevenue(rev);
    };

    const calculateExpenses = (data) => {
        let exp = {};
        paymentMethods.forEach(m => exp[m] = 0);

        if (Array.isArray(data)) {
        data.forEach(e => {
            let method = e.payment_method;
            if (method === "CRDB_BANK") method = "CRDB BANK";
            if (method === "MIX_BY_YAS") method = "MIX BY YAS";
            
            if (exp.hasOwnProperty(method)) {
            exp[method] += parseFloat(e.amount || 0);
            }
        });
        }

        setExpenses(exp);
    };

    const calculateOpeningBalance = (payments, expData) => {
        let open = {};
        paymentMethods.forEach(m => open[m] = 0);

        const start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const yesterday = new Date(selectedDate);
        yesterday.setDate(yesterday.getDate() - 1);

        // Calculate from payments
        if (Array.isArray(payments)) {
        payments.forEach(p => {
            if (p.status !== "PAID") return;
            const d = new Date(p.created_at);
            
            if (d >= start && d <= yesterday) {
            let method = p.payment_method;
            if (method === "CRDB_BANK") method = "CRDB BANK";
            if (method === "MIX_BY_YAS") method = "MIX BY YAS";
            
            if (open.hasOwnProperty(method)) {
                open[method] += parseFloat(p.total_amount || 0);
            }
            }
        });
        }

        // Subtract expenses
        if (Array.isArray(expData)) {
        expData.forEach(e => {
            const d = new Date(e.created_at);
            
            if (d >= start && d <= yesterday) {
            let method = e.payment_method;
            if (method === "CRDB_BANK") method = "CRDB BANK";
            if (method === "MIX_BY_YAS") method = "MIX BY YAS";
            
            if (open.hasOwnProperty(method)) {
                open[method] -= parseFloat(e.amount || 0);
            }
            }
        });
        }

        setOpening(open);
    };

    const sum = (obj) => Object.values(obj).reduce((a, b) => a + (b || 0), 0);

    const totalRevenue = sum(revenue);
    const totalExpenses = sum(expenses);

    const closing = {};
    paymentMethods.forEach(m => {
        closing[m] = (opening[m] || 0) + (revenue[m] || 0) - (expenses[m] || 0);
    });
    const closingTotal = sum(closing);

    const format = (n) => n?.toLocaleString() || "0";

    // Export Excel with professional formatting
    const exportExcel = () => {
        const wsData = [
        ["REVENUE REPORT"],
        [`Date: ${selectedDate.toLocaleDateString()}`],
        [`Generated: ${new Date().toLocaleString()}`],
        [],
        ["REVENUE SUMMARY"],
        ["Payment Method", "Opening Balance", "Revenue", "Expenses", "Closing Balance"],
        ];

        // Add account data
        paymentMethods.forEach(method => {
        wsData.push([
            method,
            format(opening[method]),
            format(revenue[method]),
            format(expenses[method]),
            format(closing[method])
        ]);
        });

        // Add totals
        wsData.push([
        "TOTAL",
        format(sum(opening)),
        format(totalRevenue),
        format(totalExpenses),
        format(closingTotal)
        ]);

        wsData.push([], [], ["EXPENSE BREAKDOWN"]);
        wsData.push(["Payment Method", "Amount"]);

        // Add expense breakdown
        paymentMethods.forEach(method => {
        if (expenses[method] > 0) {
            wsData.push([method, format(expenses[method])]);
        }
        });
        wsData.push(["TOTAL EXPENSES", format(totalExpenses)]);

        wsData.push([], [], ["REVENUE BREAKDOWN"]);
        wsData.push(["Payment Method", "Amount"]);

        // Add revenue breakdown
        paymentMethods.forEach(method => {
        if (revenue[method] > 0) {
            wsData.push([method, format(revenue[method])]);
        }
        });
        wsData.push(["TOTAL REVENUE", format(totalRevenue)]);

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        // Set column widths
        ws['!cols'] = [
        { wch: 20 }, // Payment Method
        { wch: 15 }, // Amounts
        { wch: 15 },
        { wch: 15 },
        { wch: 15 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Revenue Report");
        
        const fileBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
        new Blob([fileBuffer], { type: "application/octet-stream" }),
        `Revenue_Report_${formatDate(selectedDate)}.xlsx`
        );
    };

    return (
        <div className="reportPage">
        <div className="reportHeader">
            <h2>Revenue Report</h2>
            <div className="headerActions">
            <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="dd/MM/yyyy"
            />
            <button onClick={exportExcel} className="exportBtn" disabled={loading}>
                📊 Export Excel
            </button>
            <button onClick={fetchReport} className="refreshBtn" disabled={loading}>
                🔄 Refresh
            </button>
            </div>
        </div>

        {loading && (
            <div className="loadingOverlay">
            <div className="spinner"></div>
            <p>Loading report data...</p>
            </div>
        )}

        <div className="reportContainer">
            <table className="reportTable">
            <thead>
                <tr>
                <th>Date</th>
                <th>Revenue Report</th>
                <th>Cash</th>
                <th>MPESA</th>
                <th>MIX BY YAS</th>
                <th>SELECOM</th>
                <th>CRDB BANK</th>
                <th>Total</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                <td>{new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).toLocaleDateString()}</td>
                <td>Opening balance</td>
                <td>{format(opening["CASH"])}</td>
                <td>{format(opening["M-PESA"])}</td>
                <td>{format(opening["MIX BY YAS"])}</td>
                <td>{format(opening["SELECOM"])}</td>
                <td>{format(opening["CRDB BANK"])}</td>
                <td className="total-cell">{format(sum(opening))}</td>
                </tr>
                <tr>
                <td>{selectedDate.toLocaleDateString()}</td>
                <td>Revenue</td>
                <td>{format(revenue["CASH"])}</td>
                <td>{format(revenue["M-PESA"])}</td>
                <td>{format(revenue["MIX BY YAS"])}</td>
                <td>{format(revenue["SELECOM"])}</td>
                <td>{format(revenue["CRDB BANK"])}</td>
                <td className="total-cell revenue">{format(totalRevenue)}</td>
                </tr>
                <tr>
                <td>{selectedDate.toLocaleDateString()}</td>
                <td>Expenses</td>
                <td>{format(expenses["CASH"])}</td>
                <td>{format(expenses["M-PESA"])}</td>
                <td>{format(expenses["MIX BY YAS"])}</td>
                <td>{format(expenses["SELECOM"])}</td>
                <td>{format(expenses["CRDB BANK"])}</td>
                <td className="total-cell expense">{format(totalExpenses)}</td>
                </tr>
                <tr className="closingRow">
                <td>{selectedDate.toLocaleDateString()}</td>
                <td>Closing balance</td>
                <td className="closing-value">{format(closing["CASH"])}</td>
                <td className="closing-value">{format(closing["M-PESA"])}</td>
                <td className="closing-value">{format(closing["MIX BY YAS"])}</td>
                <td className="closing-value">{format(closing["SELECOM"])}</td>
                <td className="closing-value">{format(closing["CRDB BANK"])}</td>
                <td className="total-cell closing-total">{format(closingTotal)}</td>
                </tr>
            </tbody>
            </table>

            <div className="expenseSide">
            <h3>Expense Breakdown</h3>
            <p className="expenseDate">{selectedDate.toLocaleDateString()}</p>
            <table>
                <tbody>
                <tr>
                    <td>Cash</td>
                    <td className="amount">{format(expenses["CASH"])}</td>
                </tr>
                <tr>
                    <td>M-Pesa</td>
                    <td className="amount">{format(expenses["M-PESA"])}</td>
                </tr>
                <tr>
                    <td>Mix By Yas</td>
                    <td className="amount">{format(expenses["MIX BY YAS"])}</td>
                </tr>
                <tr>
                    <td>Selecom</td>
                    <td className="amount">{format(expenses["SELECOM"])}</td>
                </tr>
                <tr>
                    <td>CRDB Bank</td>
                    <td className="amount">{format(expenses["CRDB BANK"])}</td>
                </tr>
                <tr className="totalRow">
                    <td>Total Expenses</td>
                    <td className="amount total">{format(totalExpenses)}</td>
                </tr>
                </tbody>
            </table>

            <div className="revenue-summary">
                <h4>Revenue Summary</h4>
                <table>
                <tbody>
                    <tr>
                    <td>Total Revenue</td>
                    <td className="amount revenue">{format(totalRevenue)}</td>
                    </tr>
                    <tr>
                    <td>Net Profit</td>
                    <td className={`amount ${totalRevenue - totalExpenses >= 0 ? 'profit' : 'loss'}`}>
                        {format(totalRevenue - totalExpenses)}
                    </td>
                    </tr>
                    <tr>
                    <td>Profit Margin</td>
                    <td className="amount">
                        {totalRevenue > 0 ? ((totalRevenue - totalExpenses) / totalRevenue * 100).toFixed(2) : 0}%
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>
            </div>
        </div>
        </div>
    );
    }